import { CoreExpression, Identifier } from 'jsep';
import { getAge } from '../date';
import { ActivityResponseValue, ActivityType } from '../model/activity';
import { Property, PropertyType } from '../model/entity-type';
import { Expression } from '../model/expression';
import { Role } from '../model/organization';
import {
  AreaOfPersistentUnemploymentLookupItem,
  ethnicityOptions,
  genderOptions,
  maritalStatusOptions,
  raceOptions,
  RucaLookupItem,
  sexualOrientationOptions
} from '../model/person';
import { roundNumber } from '../number';

export type BasicProperty = Pick<
  Property,
  'property_id' | 'type' | 'choices' | 'choice_type'
>;

export const standardProperties: Record<string, BasicProperty> = {
  ORIGINAL_ACTIVITY_STATUS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_START_DATE: {
    property_id: 0,
    type: 'date',
    choice_type: 'none'
  },
  ORIGINAL_ACTIVITY_END_DATE: {
    property_id: 0,
    type: 'date',
    choice_type: 'none'
  },
  ACTIVITY_STATUS: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  ACTIVITY_CREATE_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_START_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_END_DATE: { property_id: 0, type: 'date', choice_type: 'none' },
  ACTIVITY_CREATE_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_CREATE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  ACTIVITY_ASSIGNEE_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_LAST_NAME: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_MIDDLE_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_DOB: { property_id: 0, type: 'date', choice_type: 'none' },
  PERSON_SSN: { property_id: 0, type: 'varchar100', choice_type: 'none' },
  PERSON_VETERAN_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_MEDICAID_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_SCSEP_PARTICIPANT_ID: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_GENDER: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: genderOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_SEXUAL_ORIENTATION: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: sexualOrientationOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_RACE: {
    property_id: 0,
    type: 'int',
    choice_type: 'multiple',
    choices: raceOptions.map(({ choice_id, label, exclusive }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive
    }))
  },
  PERSON_ETHNICITY: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: ethnicityOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_MARITAL_STATUS: {
    property_id: 0,
    type: 'int',
    choice_type: 'single',
    choices: maritalStatusOptions.map(({ choice_id, label }) => ({
      choice_id,
      score: 0,
      label,
      prompt: '',
      exclusive: false
    }))
  },
  PERSON_PRIMARY_PHONE_NUMBER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_EMAIL_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_STREET_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_SUB_ADDRESS: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_CITY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_STATE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_ZIP: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_ZIP_EXT: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  PERSON_PRIMARY_RESIDENCE_COUNTY: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_LAST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_FIRST_NAME: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_USER_ROLE: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  },
  CURRENT_ORGANIZATION: {
    property_id: 0,
    type: 'varchar100',
    choice_type: 'none'
  }
};

const standardPropertiesById: Record<number, BasicProperty> = {};

Object.keys(standardProperties).forEach((key, i) => {
  const property_id = i * -1 - 1;
  standardProperties[key].property_id = property_id;
  standardPropertiesById[property_id] = standardProperties[key];
});

export interface ActivityExpressionStandardPropertyValues {
  original_activity_status: string;
  original_activity_start_date: string | null;
  original_activity_end_date: string | null;
  activity_status: string;
  activity_create_date: string;
  activity_start_date: string;
  activity_end_date: string | null;
  activity_create_user: string;
  activity_create_organization: string;
  activity_assignee_user: string | null;
  activity_assignee_organization: string | null;
  person_last_name: string | null;
  person_first_name: string | null;
  person_middle_name: string | null;
  person_dob: string | null;
  person_ssn: string | null;
  person_veteran_id: string | null;
  person_medicaid_id: string | null;
  person_scsep_participant_id: string | null;
  person_gender: number | null;
  person_sexual_orientation: number | null;
  person_race: number | null;
  person_ethnicity: number | null;
  person_marital_status: number | null;
  person_primary_phone_number: string | null;
  person_primary_email_address: string | null;
  person_primary_residence_street_address: string | null;
  person_primary_residence_sub_address: string | null;
  person_primary_residence_city: string | null;
  person_primary_residence_region_abbr: string | null;
  person_primary_residence_postal_code: string | null;
  person_primary_residence_postal_code_ext: string | null;
  person_primary_residence_subregion: string | null;
  // current_user_last_name: string;
  // current_user_first_name: string;
  current_user: string;
  current_user_role: Role;
  current_organization: string;
}

export interface DashboardExpressionStandardPropertyValues {
  // current_user_last_name: string;
  // current_user_first_name: string;
  current_user: string;
  current_user_role: Role;
  current_organization: string;
}

function parseExpressionDate(date: string) {
  if (!date) {
    return new Date(NaN);
  }
  switch (date.length) {
    case 23:
      return new Date(date + 'Z');
    case 10:
      return new Date(date + 'T00:00:00.000Z');
    default:
      return new Date(date);
  }
}

export class ActivityExpressionScope {
  public hasVisiblePredecessors = false;
  private readonly standardResponses: Record<
    string,
    null | ActivityResponseValue
  >;

  constructor(
    standardPropertyValues:
      | ActivityExpressionStandardPropertyValues
      | DashboardExpressionStandardPropertyValues,
    private readonly responses: Record<string, null | ActivityResponseValue>,
    private readonly properties: Record<number, Property>,
    private readonly rucaLookup: RucaLookupItem[],
    private readonly areaOfPersistentUnemployment: AreaOfPersistentUnemploymentLookupItem[],
    public readonly identifier_values: Record<
      string,
      null | ActivityResponseValue
    >,
    private readonly canViewActivityTypes: ActivityType[] | undefined
  ) {
    if ('original_activity_status' in standardPropertyValues) {
      this.standardResponses = {
        [`p${standardProperties.ORIGINAL_ACTIVITY_STATUS.property_id}`]:
          standardPropertyValues.original_activity_status,
        [`p${standardProperties.ORIGINAL_ACTIVITY_START_DATE.property_id}`]:
          standardPropertyValues.original_activity_start_date,
        [`p${standardProperties.ORIGINAL_ACTIVITY_END_DATE.property_id}`]:
          standardPropertyValues.original_activity_end_date,
        [`p${standardProperties.ACTIVITY_STATUS.property_id}`]:
          standardPropertyValues.activity_status,
        [`p${standardProperties.ACTIVITY_CREATE_DATE.property_id}`]:
          standardPropertyValues.activity_create_date,
        [`p${standardProperties.ACTIVITY_START_DATE.property_id}`]:
          standardPropertyValues.activity_start_date,
        [`p${standardProperties.ACTIVITY_END_DATE.property_id}`]:
          standardPropertyValues.activity_end_date,
        [`p${standardProperties.ACTIVITY_CREATE_USER.property_id}`]:
          standardPropertyValues.activity_create_user,
        [`p${standardProperties.ACTIVITY_CREATE_ORGANIZATION.property_id}`]:
          standardPropertyValues.activity_create_organization,
        [`p${standardProperties.ACTIVITY_ASSIGNEE_USER.property_id}`]:
          standardPropertyValues.activity_assignee_user,
        [`p${standardProperties.ACTIVITY_ASSIGNEE_ORGANIZATION.property_id}`]:
          standardPropertyValues.activity_assignee_organization,
        [`p${standardProperties.PERSON_LAST_NAME.property_id}`]:
          standardPropertyValues.person_last_name,
        [`p${standardProperties.PERSON_FIRST_NAME.property_id}`]:
          standardPropertyValues.person_first_name,
        [`p${standardProperties.PERSON_MIDDLE_NAME.property_id}`]:
          standardPropertyValues.person_middle_name,
        [`p${standardProperties.PERSON_DOB.property_id}`]:
          standardPropertyValues.person_dob,
        [`p${standardProperties.PERSON_SSN.property_id}`]:
          standardPropertyValues.person_ssn,
        [`p${standardProperties.PERSON_VETERAN_ID.property_id}`]:
          standardPropertyValues.person_veteran_id,
        [`p${standardProperties.PERSON_MEDICAID_ID.property_id}`]:
          standardPropertyValues.person_medicaid_id,
        [`p${standardProperties.PERSON_SCSEP_PARTICIPANT_ID.property_id}`]:
          standardPropertyValues.person_scsep_participant_id,
        [`p${standardProperties.PERSON_GENDER.property_id}`]:
          standardPropertyValues.person_gender,
        [`p${standardProperties.PERSON_SEXUAL_ORIENTATION.property_id}`]:
          standardPropertyValues.person_sexual_orientation,
        [`p${standardProperties.PERSON_RACE.property_id}`]:
          standardPropertyValues.person_race,
        [`p${standardProperties.PERSON_ETHNICITY.property_id}`]:
          standardPropertyValues.person_ethnicity,
        [`p${standardProperties.PERSON_MARITAL_STATUS.property_id}`]:
          standardPropertyValues.person_marital_status,
        [`p${standardProperties.PERSON_PRIMARY_PHONE_NUMBER.property_id}`]:
          standardPropertyValues.person_primary_phone_number,
        [`p${standardProperties.PERSON_PRIMARY_EMAIL_ADDRESS.property_id}`]:
          standardPropertyValues.person_primary_email_address,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_STREET_ADDRESS.property_id}`]:
          standardPropertyValues.person_primary_residence_street_address,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_SUB_ADDRESS.property_id}`]:
          standardPropertyValues.person_primary_residence_sub_address,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_CITY.property_id}`]:
          standardPropertyValues.person_primary_residence_city,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_STATE.property_id}`]:
          standardPropertyValues.person_primary_residence_region_abbr,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_ZIP.property_id}`]:
          standardPropertyValues.person_primary_residence_postal_code,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_ZIP_EXT.property_id}`]:
          standardPropertyValues.person_primary_residence_postal_code_ext,
        [`p${standardProperties.PERSON_PRIMARY_RESIDENCE_COUNTY.property_id}`]:
          standardPropertyValues.person_primary_residence_subregion
      };
    } else {
      this.standardResponses = {};
    }
    Object.assign(this.standardResponses, {
      // [`p${standardProperties.CURRENT_USER_LAST_NAME.property_id}`]:
      //   standardPropertyValues.current_user_last_name,
      // [`p${standardProperties.CURRENT_USER_FIRST_NAME.property_id}`]:
      //   standardPropertyValues.current_user_first_name,
      [`p${standardProperties.CURRENT_USER.property_id}`]:
        standardPropertyValues.current_user,
      [`p${standardProperties.CURRENT_USER_ROLE.property_id}`]:
        standardPropertyValues.current_user_role,
      [`p${standardProperties.CURRENT_ORGANIZATION.property_id}`]:
        standardPropertyValues.current_organization
    });
  }

  contextualIdentifiers = {
    DAY: 0,
    MONTH: 1,
    YEAR: 2,
    QUARTER: 3,
    WEEK: 4
  };

  functions = {
    SELECTED: (property_id: number, ...codes: number[]) => {
      const response =
        property_id > 0
          ? this.responses[`p${property_id}`]
          : this.standardResponses[`p${property_id}`];
      if (response === undefined || response === null) {
        return false;
      }
      if (typeof response !== 'number') {
        throw new Error(
          `Expected numeric response. ${JSON.stringify(response)}`
        );
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type === 'multiple') {
        return (codes.reduce((m, shift) => m + 2 ** shift, 0) & response) !== 0;
      } else {
        return codes.some(code => response === code);
      }
    },

    COUNTIF: (...args: boolean[]) => {
      let count = 0;
      for (const arg of args) {
        if (arg) {
          count++;
        }
      }
      return count;
    },

    ANY: (...args: any[]) => {
      return args.some(x => x);
    },

    VALUE: (property_id: number) => {
      const response =
        property_id > 0
          ? this.responses[`p${property_id}`]
          : this.standardResponses[`p${property_id}`];
      if (response !== null && response !== undefined) {
        return response;
      } else {
        return null;
      }
    },

    ANSWERED: (property_id: number) => {
      const response =
        property_id > 0
          ? this.responses[`p${property_id}`]
          : this.standardResponses[`p${property_id}`];
      return response !== null && response !== undefined && response !== '';
    },

    SCORE: (property_id: number, ...choice_ids: number[]) => {
      const response =
        property_id > 0
          ? this.responses[`p${property_id}`]
          : this.standardResponses[`p${property_id}`];
      if (response === undefined || response === null) {
        return NaN;
      }
      if (typeof response !== 'number') {
        throw new Error(
          `Expected numeric response. ${JSON.stringify(response)}`
        );
      }
      const property =
        this.properties[property_id] ?? standardPropertiesById[property_id];
      if (!property) {
        throw new Error(
          `Expression references ${property_id} but wasn't included in scope.`
        );
      }
      if (property.choice_type === 'multiple') {
        return (
          property.choices
            ?.filter(
              c => choice_ids.length === 0 || choice_ids.includes(c.choice_id)
            )
            ?.filter(c => ((2 ** c.choice_id) & response) !== 0)
            .reduce((sum, c) => sum + c.score, 0) ?? NaN
        );
      } else if (property.choice_type === 'single') {
        return (
          property.choices
            ?.filter(
              c => choice_ids.length === 0 || choice_ids.includes(c.choice_id)
            )
            ?.find(c => c.choice_id === response)?.score ?? NaN
        );
      } else {
        return response;
      }
    },

    ROUND: (value: number, digits = 0) => {
      if (typeof value !== 'number') {
        return null;
      }
      return roundNumber(value, digits);
    },

    FLOOR: (value: number) => {
      if (typeof value !== 'number') {
        return null;
      }
      return Math.floor(value);
    },

    CEILING: (value: number) => {
      if (typeof value !== 'number') {
        return null;
      }
      return Math.ceil(value);
    },

    MIN: (v1: number, v2: number) => {
      if (typeof v1 !== 'number' || typeof v2 !== 'number') {
        return null;
      }
      return Math.min(v1, v2);
    },

    MAX: (v1: number, v2: number) => {
      if (typeof v1 !== 'number' || typeof v2 !== 'number') {
        return null;
      }
      return Math.max(v1, v2);
    },

    ABS: (value: number) => {
      if (typeof value !== 'number') {
        return null;
      }
      return Math.abs(value);
    },

    NOW: () =>
      new Date(
        new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
      ).toISOString(),

    AGE: (dob: string, asOf: string) => {
      const parsedDob = parseExpressionDate(dob);
      const parsedAsOf = parseExpressionDate(asOf);
      if (!isFinite(parsedDob.getTime()) || !isFinite(parsedAsOf.getTime())) {
        return null;
      }
      return getAge(parsedDob, parsedAsOf).years;
    },

    DATEADD: (part: number, value: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        typeof value !== 'number' ||
        !Number.isInteger(value) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      // https://www.npmjs.com/package/dateadd?activeTab=code
      switch (part) {
        case DAY:
        case WEEK:
          if (part === WEEK) {
            value *= 7;
          }
          return new Date(
            parsed.setUTCDate(parsed.getUTCDate() + value)
          ).toISOString();
        case MONTH:
        case QUARTER:
          if (part === QUARTER) {
            value *= 3;
          }
          return new Date(
            parsed.setUTCMonth(parsed.getUTCMonth() + value)
          ).toISOString();
        case YEAR:
          return new Date(
            parsed.setUTCFullYear(parsed.getUTCFullYear() + value)
          ).toISOString();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATEDIFF: (part: number, start: string, end: string) => {
      const parsedStart = parseExpressionDate(start);
      const parsedEnd = parseExpressionDate(end);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        !isFinite(parsedStart.getTime()) ||
        !isFinite(parsedEnd.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return Math.floor(
            (parsedEnd.getTime() - parsedStart.getTime()) / 86400000
          );
        case WEEK: {
          const weeks = Math.floor(
            (parsedEnd.getTime() - parsedStart.getTime()) / 604800000
          );
          return (
            weeks + (parsedStart.getUTCDay() > parsedEnd.getUTCDay() ? 1 : 0)
          );
        }
        case MONTH:
        case QUARTER: {
          let result =
            (parsedEnd.getUTCFullYear() - parsedStart.getUTCFullYear()) * 12 +
            parsedEnd.getUTCMonth() -
            parsedStart.getUTCMonth();
          if (part === QUARTER) {
            result = Math.floor(result / 3);
          }
          return result;
        }
        case YEAR:
          return parsedEnd.getUTCFullYear() - parsedStart.getUTCFullYear();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATEPART: (part: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return parsed.getUTCDate();
        case WEEK: {
          const jan1 = new Date(Date.UTC(parsed.getUTCFullYear()));
          const dayOfYear = (parsed.getTime() - jan1.getTime()) / 86400000;
          return (
            Math.floor(dayOfYear / 7) +
            1 +
            (jan1.getUTCDay() > parsed.getUTCDay() ? 1 : 0)
          );
        }
        case MONTH:
          return parsed.getUTCMonth() + 1;
        case QUARTER:
          return Math.floor(parsed.getUTCMonth() / 3) + 1;
        case YEAR:
          return parsed.getUTCFullYear();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    DATETRUNC: (part: number, date: string) => {
      const parsed = parseExpressionDate(date);
      const { DAY, MONTH, QUARTER, YEAR, WEEK } = this.contextualIdentifiers;
      if (
        ![DAY, MONTH, QUARTER, YEAR, WEEK].includes(part) ||
        !isFinite(parsed.getTime())
      ) {
        return null;
      }
      switch (part) {
        case DAY:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              parsed.getUTCMonth(),
              parsed.getUTCDate()
            )
          ).toISOString();
        case WEEK:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              parsed.getUTCMonth(),
              parsed.getUTCDate() - parsed.getUTCDay()
            )
          ).toISOString();
        case MONTH:
          return new Date(
            Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1)
          ).toISOString();
        case QUARTER:
          return new Date(
            Date.UTC(
              parsed.getUTCFullYear(),
              Math.floor(parsed.getUTCMonth() / 3) * 3,
              1
            )
          ).toISOString();
        case YEAR:
          return new Date(
            Date.UTC(parsed.getUTCFullYear(), 0, 1)
          ).toISOString();
        default:
          throw new Error(`Unexpected date part: ${part}`);
      }
    },

    EOMONTH: (date: string) => {
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      const endOfMonth = new Date(
        Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0)
      );
      return endOfMonth.toISOString();
    },

    DATE: (value: string) => {
      const parsed = parseExpressionDate(value);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return parsed.toISOString();
    },

    IF: (condition: boolean, trueValue: unknown, falseValue: unknown) => {
      return condition ? trueValue : falseValue;
    },

    LOWER: (s: string) => {
      return s?.toLowerCase() ?? null;
    },

    UPPER: (s: string) => {
      return s?.toUpperCase() ?? null;
    },

    RURAL: (date: string) => {
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return (
        (this.rucaLookup.find(
          x => parseExpressionDate(x.start_date).getTime() <= parsed.getTime()
        )?.primary_code ?? 0) >= 4
      );
    },

    AREA_OF_PERSISTENT_UNEMPLOYMENT: (date: string) => {
      const parsed = parseExpressionDate(date);
      if (!isFinite(parsed.getTime())) {
        return null;
      }
      return (
        this.areaOfPersistentUnemployment.find(
          x => parseExpressionDate(x.start_date).getTime() <= parsed.getTime()
        )?.persistent_unemployment ?? false
      );
    },

    HAS_VISIBLE_PREDECESSORS: () => {
      // console.log(`HAS_VISIBLE_PREDECESSORS: ${this.hasVisiblePredecessors}`);
      return this.hasVisiblePredecessors;
    },

    DEBUG: (label: string, value: unknown) => {
      console.log(`EXPRESSION DEBUG ${label}: ${value}`);
      return value;
    },

    CAN_VIEW_ACTIVITY_TYPE: (type: string) => {
      return this.canViewActivityTypes?.includes(type as ActivityType) ?? null;
    },

    MID(value: string, start: number, length?: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof start !== 'number' || !isFinite(start)) {
        return null;
      }
      if (
        length !== undefined &&
        (typeof length !== 'number' || !isFinite(length))
      ) {
        return null;
      }
      if (length !== undefined && length <= 0) {
        return '';
      }
      start = start < 1 ? 1 : start;
      return value.substr(start - 1, length);
    },

    LEFT(value: string, length: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof length !== 'number' || !isFinite(length)) {
        return null;
      }
      if (length <= 0) {
        return '';
      }
      return value.substr(0, length);
    },

    RIGHT(value: string, length: number) {
      if (typeof value !== 'string') {
        return null;
      }
      if (typeof length !== 'number' || !isFinite(length)) {
        return null;
      }
      if (length <= 0) {
        return '';
      }
      return value.slice(-1 * length);
    },

    MATCH<T>(value: T, ...args: T[]) {
      if (
        value === null ||
        value === undefined ||
        (typeof value !== 'string' && typeof value !== 'number') ||
        (typeof value === 'number' && !isFinite(value))
      ) {
        return null;
      }
      return args.includes(value);
    },

    SWITCH(value: unknown, ...args: unknown[]) {
      if (value === null || value === undefined) {
        return null;
      }
      for (let i = 0; i < args.length; i += 2) {
        if (args[i] === value) {
          return args[i + 1];
        }
      }
      return args.length % 2 === 0 ? null : args[args.length - 1];
    },

    IFS(...args: unknown[]) {
      for (let i = 0; i < args.length; i += 2) {
        if (args[i]) {
          return args[i + 1];
        }
      }
      return null;
    }
  };
}

// note: it may not be necessary to coerce values as is done here.
export function evaluateActivityExpression(
  propertyType: PropertyType,
  { type, ast }: Expression,
  scope: ActivityExpressionScope
): ActivityResponseValue | null {
  const result = evaluateExpression(ast, scope);
  if (result === null) {
    return null;
  }
  switch (type) {
    case 'boolean':
      // int used because there's no boolean/bit response table.
      return Boolean(result) ? 1 : 0;
    case 'int': {
      const value = Math.floor(Number(result));
      if (!isFinite(value)) {
        return null;
      }
      return value;
    }
    // @ts-ignore
    case 'currency': // todo: Fix currency type expressions in db, if any. Should be decimal.
    case 'decimal': {
      const value = Number(result);
      if (!isFinite(value)) {
        return null;
      }
      return propertyType === 'currency' ? roundNumber(value, 2) : value;
    }
    case 'string':
      return String(result);
    case 'date':
    case 'datetime': {
      const value = parseExpressionDate(result);
      if (!isFinite(value.getTime())) {
        return null;
      }
      return value.toISOString();
    }
    default:
      throw new Error(`unsupported expression type "${type}"`);
  }
}

const binops: Record<string, { (a: any, b: any): any }> = {
  '||': function (a, b) {
    return a || b;
  },
  '&&': function (a, b) {
    return a && b;
  },
  // '|': function (a, b) {
  //   return a | b;
  // },
  // '^': function (a, b) {
  //   return a ^ b;
  // },
  // '&': function (a, b) {
  //   return a & b;
  // },
  '==': function (a, b) {
    return a == b;
  },
  '!=': function (a, b) {
    return a != b;
  },
  // '===': function (a, b) {
  //   return a === b;
  // },
  // '!==': function (a, b) {
  //   return a !== b;
  // },
  '<': function (a, b) {
    return a < b;
  },
  '>': function (a, b) {
    return a > b;
  },
  '<=': function (a, b) {
    return a <= b;
  },
  '>=': function (a, b) {
    return a >= b;
  },
  // '<<': function (a, b) {
  //   return a << b;
  // },
  // '>>': function (a, b) {
  //   return a >> b;
  // },
  // '>>>': function (a, b) {
  //   return a >>> b;
  // },
  '+': function (a, b) {
    return a + b;
  },
  '-': function (a, b) {
    return a - b;
  },
  '*': function (a, b) {
    return a * b;
  },
  '/': function (a, b) {
    return a / b;
  },
  '%': function (a, b) {
    return a % b;
  }
};

const unops: Record<string, { (a: any): any }> = {
  '-': function (a) {
    return -a;
  },
  // '+': function (a) {
  //   return +a;
  // },
  // '~': function (a) {
  //   return ~a;
  // },
  '!': function (a) {
    return !a;
  }
};

/*
function evaluateArray(list: any[], context: object) {
  return list.map(function (v) {
    return legacyEvaluate(v, context);
  });
}

function evaluateMember(node: MemberExpression, context: object) {
  const object = legacyEvaluate(node.object as CoreExpression, context);
  let key: string;
  if (node.computed) {
    key = legacyEvaluate(node.property as CoreExpression, context);
  } else {
    key = (node.property as Identifier).name;
  }
  if (/^(?:__proto__|prototype|constructor)$/.test(key)) {
    throw Error(`Access to member "${key}" disallowed.`);
  }
  return [object, object[key]];
}
*/

export function evaluateExpression(
  node: CoreExpression,
  scope: ActivityExpressionScope
): any {
  switch (node.type) {
    // case 'ArrayExpression':
    //   return evaluateArray(node.elements, context);

    case 'BinaryExpression': {
      const left = evaluateExpression(node.left as CoreExpression, scope);
      if (node.operator === '||') {
        return left || evaluateExpression(node.right as CoreExpression, scope);
      } else if (node.operator === '&&') {
        return left && evaluateExpression(node.right as CoreExpression, scope);
      }
      if ((typeof left === 'number' && !isFinite(left)) || left === null) {
        return null;
      }
      const right = evaluateExpression(node.right as CoreExpression, scope);
      if ((typeof right === 'number' && !isFinite(right)) || right === null) {
        return null;
      }
      {
        // hack: ensure string comparisons of date strings use equal length strings. Need to use Date instances internally.
        const dateString = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}\.\d+Z)?$/i;
        if (
          typeof left === 'string' &&
          typeof right === 'string' &&
          left.length !== right.length &&
          dateString.test(left) &&
          dateString.test(right)
        ) {
          return binops[node.operator](
            (left + 'T00:00:00.000Z').substr(0, 24),
            (right + 'T00:00:00.000Z').substr(0, 24)
          );
        }
      }
      return binops[node.operator](left, right);
    }
    case 'CallExpression':
      const callee = node.callee as Identifier;
      if (callee.type !== 'Identifier') {
        return undefined;
      }
      // Coerce callee name to uppercase.
      callee.name = callee.name.toUpperCase();
      const fn = evaluateExpression(callee, scope);
      if (typeof fn !== 'function') {
        return undefined;
      }
      return fn.apply(
        null,
        node.arguments.map(node =>
          evaluateExpression(node as CoreExpression, scope)
        )
      );

    // case 'ConditionalExpression':
    //   return legacyEvaluate(node.test as CoreExpression, context)
    //     ? legacyEvaluate(node.consequent as CoreExpression, context)
    //     : legacyEvaluate(node.alternate as CoreExpression, context);

    case 'Identifier': {
      const name = node.name.toUpperCase();
      if (Object.hasOwn(standardProperties, name)) {
        return standardProperties[name].property_id;
      }
      if (Object.hasOwn(scope.functions, name)) {
        return (scope.functions as any)[name];
      }
      if (Object.hasOwn(scope.contextualIdentifiers, name)) {
        return (scope.contextualIdentifiers as any)[name];
      }
      if (Object.hasOwn(scope.identifier_values, name)) {
        return (scope.identifier_values as any)[name];
      }
      return null;
    }
    case 'Literal':
      return node.value;

    // case 'MemberExpression':
    //   return evaluateMember(node, context)[1];

    // case 'ThisExpression':
    //   return context;

    case 'UnaryExpression':
      return unops[node.operator](
        evaluateExpression(node.argument as CoreExpression, scope)
      );

    default:
      throw new Error(`Unexpected node type "${(node as any).type}".`);
  }
}
